
import React, { useState, useRef, useEffect } from "react";

const FileUpload = () => {
  // --- Authentication / Authorization State ---
  const [isAuthorized, setIsAuthorized] = useState(null); // null = checking, true = authorized, false = denied

  // --- Upload State ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  
  const fileInputRef = useRef(null);

  // Run once on component mount to check the user's role
  useEffect(() => {
    try {
      const userStorage = localStorage.getItem("user");
      if (userStorage) {
        const parsedUser = JSON.parse(userStorage);
        // Check if the role exactly matches "SUPER_ONBOARDING"
        if (parsedUser?.role === "SUPER_ONBOARDING") {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } else {
        // No user found in storage
        setIsAuthorized(false);
      }
    } catch (e) {
      console.error("Failed to parse user data:", e);
      setIsAuthorized(false);
    }
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setUploadResult(null); 
    }
  };

  const clearSelection = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select an Excel file first.");
      return;
    }

    setLoading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    // Re-grab token for the API call
    let token = "";
    try {
      const userStorage = localStorage.getItem("user");
      if (userStorage) {
        const parsedUser = JSON.parse(userStorage);
        token = parsedUser?.token || "";
      }
    } catch (e) {
      console.error("Failed to parse user data for token:", e);
    }

    try {
      const response = await fetch("https://portal.bingopayments.in/api/bpay/payout/bulk/callback/upload", {
        method: "POST",
        headers: {
          "Authorization": token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `HTTP Error: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (jsonError) {
          // Fallback
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      setUploadResult(result);
      setShowPopup(true);
      clearSelection();

    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.message || "Failed to upload the file.");
    } finally {
      setLoading(false);
    }
  };

  // 1. Render Loading State while checking permissions
  if (isAuthorized === null) {
    return (
      <div className="w-full max-w-md mx-auto p-8 text-center flex flex-col items-center justify-center">
        <svg className="animate-spin h-8 w-8 text-[#12345A] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-[#12345A] font-medium animate-pulse">Verifying permissions...</p>
      </div>
    );
  }

  // 2. Render Error State if validation fails
  if (isAuthorized === false) {
    return (
      <div className="w-full max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden border border-red-100 p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#12345A] mb-2">Access Denied</h2>
        <p className="text-gray-600 text-sm">
          You do not have the required permissions to view this page.
        </p>
      </div>
    );
  }

  // 3. Render Main Component if validation passes
  return (
    <>
      <div className="w-full max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-[#12345A] px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white m-0 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Callback File
          </h2>
          
        </div>

        <div className="p-6">
          {/* Upload Area */}
          <div className="mb-6">
            {!selectedFile ? (
              <label 
                htmlFor="excel-upload-input" 
                className="flex flex-col items-center justify-center w-full h-40 border-2 border-[#12345A] border-dashed rounded-lg cursor-pointer bg-[#12345A]/5 hover:bg-[#12345A]/10 transition-colors duration-200"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-10 h-10 mb-3 text-[#12345A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mb-1 text-sm text-gray-600">
                    <span className="font-bold text-[#12345A]">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 font-medium tracking-wide uppercase">Excel Files Only (.xlsx, .xls)</p>
                </div>
                <input
                  id="excel-upload-input"
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
              </label>
            ) : (
              <div className="flex items-center justify-between p-4 border border-[#12345A]/30 bg-[#12345A]/5 rounded-lg">
                <div className="flex items-center space-x-3 overflow-hidden">
                  <svg className="w-8 h-8 text-[#12345A] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm font-medium text-[#12345A] truncate">
                    {selectedFile.name}
                  </span>
                </div>
                <button 
                  onClick={clearSelection}
                  disabled={loading}
                  className="text-sm font-semibold text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 flex-shrink-0 ml-4"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-md flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Action Button */}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || loading}
            className={`w-full flex items-center justify-center py-3 px-4 rounded-lg font-bold text-white transition-all duration-200 ${
              !selectedFile || loading
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-[#12345A] hover:bg-[#0e2744] shadow-md hover:shadow-lg focus:ring-2 focus:ring-offset-2 focus:ring-[#12345A]"
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing Upload...
              </>
            ) : (
              "Upload Excel File"
            )}
          </button>

          {/* Inline Upload Results Dashboard */}
          {uploadResult && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-bold text-[#12345A] mb-3 uppercase tracking-wide border-b border-gray-200 pb-2">
                Processing Summary
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded-md shadow-sm border-l-4 border-green-500 flex flex-col">
                  <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Success</span>
                  <span className="text-2xl font-bold text-green-600">{uploadResult.success}</span>
                </div>
                <div className="bg-white p-3 rounded-md shadow-sm border-l-4 border-red-500 flex flex-col">
                  <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Failed</span>
                  <span className="text-2xl font-bold text-red-600">{uploadResult.failed}</span>
                </div>
              </div>
              
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-xs font-bold text-red-600 uppercase mb-2 block">Error Details:</span>
                  <ul className="text-sm text-gray-600 list-disc pl-4 space-y-1 max-h-32 overflow-y-auto">
                    {uploadResult.errors.map((err, index) => (
                      <li key={index}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Custom Clean Popup / Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#12345A]/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm transform transition-all text-center animate-fade-in-up">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-5">
              <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h3 className="text-xl font-extrabold text-[#12345A] mb-2">
              Upload Complete!
            </h3>
            
            <p className="text-gray-600 mb-6 text-sm">
              Your Excel file has been successfully uploaded and processed.
            </p>
            
            <button 
              onClick={() => setShowPopup(false)}
              className="w-full bg-[#12345A] hover:bg-[#0e2744] text-white font-bold py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#12345A]"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FileUpload;